import { useQuery } from '@tanstack/react-query';
import { weatherApi } from '../services/api';

export interface WeatherData {
  temperature: number;
  feels_like: number;
  humidity: number;
  pressure: number;
  wind_speed: number;
  wind_direction: number;
  uv_index: number;
  visibility: number;
  cloud_cover: number;
  precipitation_probability: number;
  weather_code: number;
  description: string;
  icon: string;
  timestamp: string;
}

// Request-Deduplication: Aktive Requests tracken
const activeRequests = new Map<string, Promise<any>>();

export function useWeather(lat?: number, lon?: number) {
  return useQuery<WeatherData>({
    queryKey: ['weather', 'current', lat, lon],
    queryFn: async () => {
      if (!lat || !lon) {
        throw new Error('Coordinates required');
      }
      
      // Request-Deduplication: Prüfen ob identischer Request bereits läuft
      const cacheKey = `weather:current:${lat}:${lon}`;
      
      if (activeRequests.has(cacheKey)) {
        return activeRequests.get(cacheKey);
      }
      
      const requestPromise = weatherApi.getCurrentWeather(lat, lon)
        .then(response => {
          activeRequests.delete(cacheKey);
          return response.data;
        })
        .catch(error => {
          activeRequests.delete(cacheKey);
          throw error;
        });
      
      activeRequests.set(cacheKey, requestPromise);
      return requestPromise;
    },
    enabled: Boolean(lat) && Boolean(lon),
    staleTime: 10 * 60 * 1000, // 10 minutes - aligned with backend cache
    refetchInterval: 15 * 60 * 1000, // Refetch every 15 minutes
  });
}

export function useForecast(lat?: number, lon?: number, days: number = 7) {
  return useQuery({
    queryKey: ['weather', 'forecast', lat, lon, days],
    queryFn: async () => {
      if (!lat || !lon) {
        throw new Error('Coordinates required');
      }
      
      // Request-Deduplication für Forecast
      const cacheKey = `weather:forecast:${lat}:${lon}:${days}`;
      
      if (activeRequests.has(cacheKey)) {
        return activeRequests.get(cacheKey);
      }
      
      const requestPromise = weatherApi.getForecast(lat, lon, days)
        .then(response => {
          activeRequests.delete(cacheKey);
          return response.data;
        })
        .catch(error => {
          activeRequests.delete(cacheKey);
          throw error;
        });
      
      activeRequests.set(cacheKey, requestPromise);
      return requestPromise;
    },
    enabled: Boolean(lat) && Boolean(lon),
    staleTime: 30 * 60 * 1000, // 30 minutes - aligned with backend cache
  });
}

export function useAirQuality(lat?: number, lon?: number) {
  return useQuery({
    queryKey: ['weather', 'air-quality', lat, lon],
    queryFn: async () => {
      if (!lat || !lon) {
        throw new Error('Coordinates required');
      }
      
      // Request-Deduplication für Air Quality
      const cacheKey = `weather:airquality:${lat}:${lon}`;
      
      if (activeRequests.has(cacheKey)) {
        return activeRequests.get(cacheKey);
      }
      
      const requestPromise = weatherApi.getAirQuality(lat, lon)
        .then(response => {
          activeRequests.delete(cacheKey);
          return response.data;
        })
        .catch(error => {
          activeRequests.delete(cacheKey);
          throw error;
        });
      
      activeRequests.set(cacheKey, requestPromise);
      return requestPromise;
    },
    enabled: Boolean(lat) && Boolean(lon),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

